import type { ReactNode, MouseEvent } from 'react';

import {
  BpkButtonV2,
  BUTTON_TYPES,
} from '@skyscanner/backpack-web/bpk-component-button';
import {
  withButtonAlignment,
  withRtlSupport,
} from '@skyscanner/backpack-web/bpk-component-icon';
import ChevronRight from '@skyscanner/backpack-web/bpk-component-icon/sm/chevron-right';
import BpkText, {
  TEXT_COLORS,
  TEXT_STYLES,
} from '@skyscanner/backpack-web/bpk-component-text';
import { cssModules } from '@skyscanner/backpack-web/bpk-react-utils';

import type { PoiDataArray } from '@shared/types/frontend/nearby-transport-response';
import type { I18nService } from '@shared/types/i18n';
import type { EntityId, Nullable } from '@shared/types/utils';

import STYLES from './PoiCard.module.scss';

import { MAX_POI_ITEM_SINGLE } from '@hotels-hotel-detail/utils/location-constants';
import { buildDetailsMapAttributes } from '@hotels-hotel-detail/utils/meaningful-behaviour-data/details-map';
import { FormattedDistance } from '@shared/ui/components/i18n';
import { withI18n } from '@shared/ui/contexts/i18n';
import { MeaningfulBehaviourDataWrapper } from '@shared/ui/contexts/meaningful-behaviour-data';

const cls = cssModules(STYLES);

const MAX_POI_ITEM = 3;

const AlignedChevronIcon = withButtonAlignment(withRtlSupport(ChevronRight));

type Props = {
  i18n: I18nService;
  hotelId: string;
  poiTab: string;
  title: ReactNode;
  poiData: PoiDataArray;
  isSingle: boolean;
  onClick: (
    e: MouseEvent,
    selectedPoi: Nullable<{ entityId: EntityId; linearDistance: number }>,
  ) => void;
  onPoiMarkerClick: (selectedEntityId: Nullable<EntityId>) => void;
};
const PoiCard = ({
  hotelId,
  i18n,
  isSingle,
  onClick,
  onPoiMarkerClick,
  poiData,
  poiTab,
  title,
}: Props) => {
  const poiDataLength = poiData.length;
  if (poiDataLength === 0) {
    return null;
  }
  const maxDistance = poiData[poiData.length - 1].linearDistance;

  return (
    <MeaningfulBehaviourDataWrapper
      {...buildDetailsMapAttributes({ hotelId, item: poiTab })}
    >
      <div className={STYLES.PoiCard__card} data-testid="details-page-poi-card">
        <BpkText
          textStyle={TEXT_STYLES.subheading}
          tagName="p"
          className={STYLES.PoiCard__cardTitle}
        >
          {title}
          <BpkText
            textStyle={TEXT_STYLES.footnote}
            className={STYLES.PoiCard__cardTitleExtra}
            color={TEXT_COLORS.textSecondary}
          >
            <FormattedDistance
              distanceMeters={maxDistance}
              options={{
                kmString: 'Distance_label_withinKm',
                milesString: 'Distance_label_withinMiles',
              }}
            />
          </BpkText>
        </BpkText>
        <div className={cls('PoiCard__poiItemContainer')}>
          {poiData
            .slice(0, isSingle ? MAX_POI_ITEM_SINGLE : MAX_POI_ITEM)
            .map(({ entityId, linearDistance, poiName }) => (
              <button
                type="button"
                className={cls('PoiCard__poiItem')}
                key={entityId}
                onClick={(e: MouseEvent) => {
                  e.stopPropagation();
                  onPoiMarkerClick(entityId);
                  onClick(e, { entityId, linearDistance });
                }}
              >
                <div className={STYLES.PoiCard__poiName}>
                  <BpkText>{poiName}</BpkText>
                </div>
                <BpkText
                  textStyle={TEXT_STYLES.footnote}
                  className={STYLES.PoiCard__poiDistance}
                  color={TEXT_COLORS.textSecondary}
                >
                  <FormattedDistance distanceMeters={linearDistance} />
                </BpkText>
              </button>
            ))}
        </div>
        <BpkButtonV2
          type={BUTTON_TYPES.link}
          className={STYLES.PoiCard__linkBtn}
          data-testid="poi-card-view-more"
          onClick={(e: MouseEvent) => {
            onPoiMarkerClick(null);
            onClick(e, null);
          }}
        >
          {poiDataLength > MAX_POI_ITEM
            ? i18n.translate('Button_viewMore')
            : i18n.translate('HotelCard_label_seeDetails')}
          <AlignedChevronIcon />
        </BpkButtonV2>
      </div>
    </MeaningfulBehaviourDataWrapper>
  );
};

export default withI18n(PoiCard);
